// 003 (FR-024, FR-025; P-JOIN-8; research R10; data-model §8): the Enter school code step's form.
// "gps 7k2" is read as GPS7K2; malformed codes get the field error without a request; an active
// school shows its card with This is my school / Change code. Reused by 004 (Change school, Link
// with school code; inside Add student with `showHeading={false}`); typed codes are always source CODE.
import { type ReactNode, useState } from "react";
import { StyleSheet, View } from "react-native";

import type { Api } from "@/api/types";
import { StateView } from "@/shell/StateView";
import { makeStyles } from "@/theme/makeStyles";
import { Button } from "@/ui/Button";
import { Form } from "@/ui/Form";
import { Text } from "@/ui/Text";
import { TextField } from "@/ui/TextField";

import { JOIN_COPY } from "./copy";
import { useJoinClient } from "./joinApi";
import { normaliseSchoolCode } from "./schoolCode";
import { type LookupResult, lookupSchool } from "./schoolLookup";
import { SchoolSummaryCard } from "./SchoolSummaryCard";

type School = Api["SchoolByCode"];
type Step =
  | { step: "editing" }
  | { step: "checking" }
  | { step: "confirm"; code: string; school: School }
  | {
      step: "refused";
      result: Exclude<LookupResult, { kind: "active" } | { kind: "invalid" }>;
    };

const useStyles = makeStyles((t) =>
  StyleSheet.create({ stack: { gap: t.space.lg } }),
);

const copy = JOIN_COPY.codeStep;

export function EnterSchoolCodeForm({
  onConfirmed,
  footer,
  showHeading = true,
}: {
  onConfirmed: (code: string, school: School) => void;
  footer?: ReactNode;
  /** False when the form sits inside another screen that has its own heading. */
  showHeading?: boolean;
}) {
  const styles = useStyles();
  const client = useJoinClient();
  const [value, setValue] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [state, setState] = useState<Step>({ step: "editing" });

  async function find() {
    if (state.step === "checking") return;
    const code = normaliseSchoolCode(value);
    setState({ step: "checking" });
    const result = await lookupSchool(
      (c) => client.getSchoolByJoinCode(c),
      code,
    );
    if (result.kind === "active") {
      setFieldError(null);
      setState({ step: "confirm", code, school: result.school });
    } else if (result.kind === "invalid") {
      setFieldError(copy.notFound);
      setState({ step: "editing" });
    } else {
      setFieldError(null);
      setState({ step: "refused", result });
    }
  }

  function change() {
    setState({ step: "editing" });
  }

  const refused = state.step === "refused" ? state.result : null;

  if (state.step === "confirm") {
    return (
      <View style={styles.stack} testID="code-confirm">
        <SchoolSummaryCard school={state.school}>
          <Button onPress={() => onConfirmed(state.code, state.school)}>
            {copy.confirm}
          </Button>
          <Button variant="outline" onPress={change}>
            {copy.change}
          </Button>
        </SchoolSummaryCard>
        {footer}
      </View>
    );
  }

  return (
    <View style={styles.stack} testID="code-step">
      {showHeading ? (
        <Text
          variant="heading"
          size="x2l"
          accessibilityRole="header"
          aria-level={1}
        >
          {copy.heading}
        </Text>
      ) : null}
      <Form onSubmit={() => void find()} accessibilityLabel={copy.heading}>
        <View style={styles.stack}>
          <TextField
            label={copy.field}
            hint={copy.hint}
            kind="code"
            value={value}
            onChangeText={(v) => {
              setValue(v);
              if (fieldError) setFieldError(null);
            }}
            error={fieldError}
            testID="school-code-field"
          />
          {refused?.kind === "notActive" ? (
            <Text role="alert">{JOIN_COPY.notActive(refused.name)}</Text>
          ) : null}
          {refused?.kind === "rateLimited" ? (
            <Text role="alert">{JOIN_COPY.rateLimited}</Text>
          ) : null}
          {refused?.kind === "error" ? (
            <StateView
              status="error"
              error={refused.error}
              onRetry={() => void find()}
            />
          ) : null}
          <Button
            onPress={() => void find()}
            pending={state.step === "checking"}
            disabled={state.step === "checking"}
          >
            {copy.find}
          </Button>
        </View>
      </Form>
      {footer}
    </View>
  );
}
